import { useState, useEffect, useCallback } from 'react';
import { Save, Bell, Globe, RefreshCw, CheckCircle, XCircle, Trash2, AlertTriangle, Loader2, Mail, Send, Clock } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

interface DataIntegrity {
  orphanedStudios: number;
  orphanedTherapists: number;
  orphanedRooms: number;
  orphanedClients: number;
  orphanedBookings: number;
  orphanedServices: number;
  usersWithoutStudio: number;
  deletedUsersWithActiveData: number;
  totalOrphaned: number;
}

interface AdminSettings {
  platform_name: string;
  support_email: string;
  default_timezone: string;
  notify_new_studios: string;
  notify_failed_payments: string;
  notify_trial_expirations: string;
  reminders_enabled: string;
}

const defaultSettings: AdminSettings = {
  platform_name: 'SereneTouch Admin',
  support_email: 'support@serenetouch.app',
  default_timezone: 'America/Chicago',
  notify_new_studios: 'true',
  notify_failed_payments: 'true',
  notify_trial_expirations: 'false',
  reminders_enabled: 'true',
};

export default function SettingsPage() {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);
  const { getToken } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  // Settings state
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);

  // Data integrity state
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [integrityData, setIntegrityData] = useState<DataIntegrity | null>(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ success: boolean; message: string } | null>(null);

  // Email test state
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [sendingBillingEmail, setSendingBillingEmail] = useState<'trial' | 'payment' | null>(null);
  const [billingEmailResult, setBillingEmailResult] = useState<{ success: boolean; message: string } | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<{
    email: { enabled: boolean; provider: string; available: boolean };
    sms: { enabled: boolean; provider: string; available: boolean };
  } | null>(null);

  // Fetch settings on mount
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Merge fetched settings with defaults (for any missing keys)
        setSettings({ ...defaultSettings, ...data.settings });
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save settings');
      }

      setSaveResult({ success: true, message: 'Settings saved successfully' });
      setTimeout(() => setSaveResult(null), 3000);
    } catch (err) {
      setSaveResult({ success: false, message: err instanceof Error ? err.message : 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncUsers = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/backfill-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync users');
      }
      setSyncResult({ success: true, message: data.message || 'Users synced successfully' });
    } catch (err) {
      setSyncResult({ success: false, message: err instanceof Error ? err.message : 'Failed to sync users' });
    } finally {
      setSyncing(false);
    }
  };

  const checkDataIntegrity = async () => {
    setIntegrityLoading(true);
    setIntegrityData(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/data-integrity`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to check data integrity');
      }
      setIntegrityData(data);
    } catch (err) {
      console.error('Data integrity check failed:', err);
    } finally {
      setIntegrityLoading(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!confirm('Are you sure you want to clean up orphaned data? This will soft-delete all data belonging to users that no longer exist in Clerk.')) {
      return;
    }
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/cleanup-orphans`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to cleanup orphans');
      }
      setCleanupResult({ success: true, message: data.message });
      // Refresh integrity data after cleanup
      checkDataIntegrity();
    } catch (err) {
      setCleanupResult({ success: false, message: err instanceof Error ? err.message : 'Failed to cleanup orphans' });
    } finally {
      setCleanupRunning(false);
    }
  };

  // Check data integrity on mount
  useEffect(() => {
    checkDataIntegrity();
    fetchNotificationStatus();
  }, []);

  const fetchNotificationStatus = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/notification-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch notification status:', err);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setEmailTestResult({ success: false, message: 'Please enter a valid email address' });
      return;
    }
    setSendingTestEmail(true);
    setEmailTestResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/test-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: testEmail }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send test email');
      }
      setEmailTestResult({ success: true, message: data.message || 'Test email sent successfully!' });
    } catch (err) {
      setEmailTestResult({ success: false, message: err instanceof Error ? err.message : 'Failed to send test email' });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendBillingTestEmail = async (type: 'trial_expiring' | 'payment_failed') => {
    if (!testEmail || !testEmail.includes('@')) {
      setBillingEmailResult({ success: false, message: 'Please enter a valid email address above first' });
      return;
    }
    setSendingBillingEmail(type === 'trial_expiring' ? 'trial' : 'payment');
    setBillingEmailResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/test-billing-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: testEmail, type }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send billing test email');
      }
      setBillingEmailResult({ success: true, message: data.message || `Test ${type} email sent!` });
    } catch (err) {
      setBillingEmailResult({ success: false, message: err instanceof Error ? err.message : 'Failed to send billing test email' });
    } finally {
      setSendingBillingEmail(null);
    }
  };

  const updateSetting = (key: keyof AdminSettings, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 text-sage animate-spin" />
          <span className="text-stone-600 dark:text-stone-400">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Settings</h1>
          <p className="text-stone-500 dark:text-stone-400 mt-1">
            Configure platform settings and preferences
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Save Result */}
      {saveResult && (
        <div className={`flex items-center gap-2 p-3 rounded-xl border ${
          saveResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          {saveResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <p className={`text-sm ${saveResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {saveResult.message}
          </p>
        </div>
      )}

      {/* General Settings */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-sage" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">General</h2>
            <p className="text-sm text-stone-500">Basic platform configuration</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              Platform Name
            </label>
            <input
              type="text"
              value={settings.platform_name}
              onChange={(e) => updateSetting('platform_name', e.target.value)}
              className="w-full px-4 py-2 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              Support Email
            </label>
            <input
              type="email"
              value={settings.support_email}
              onChange={(e) => updateSetting('support_email', e.target.value)}
              className="w-full px-4 py-2 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              Default Timezone
            </label>
            <select
              value={settings.default_timezone}
              onChange={(e) => updateSetting('default_timezone', e.target.value)}
              className="w-full px-4 py-2 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            >
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/New_York">Eastern Time (ET)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Database Operations */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Database Operations</h2>
            <p className="text-sm text-stone-500">Sync and maintenance tasks</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <div className="flex-1">
              <p className="font-medium text-stone-700 dark:text-stone-300">Sync Users from Clerk</p>
              <p className="text-sm text-stone-500">
                Backfill user data (name, email, role) from Clerk for existing users who were created before webhook sync was enabled.
              </p>
            </div>
            <button
              onClick={handleSyncUsers}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Users'}
            </button>
          </div>
          {syncResult && (
            <div className={`flex items-center gap-2 p-3 rounded-xl border ${
              syncResult.success
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              {syncResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <p className={`text-sm ${syncResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {syncResult.message}
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-stone-200 dark:border-stone-700 my-4" />

          {/* Data Integrity Check */}
          <div className="flex items-center gap-4 p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <div className="flex-1">
              <p className="font-medium text-stone-700 dark:text-stone-300">Data Integrity Check</p>
              <p className="text-sm text-stone-500">
                Scan for orphaned records (data belonging to deleted users).
              </p>
            </div>
            <button
              onClick={checkDataIntegrity}
              disabled={integrityLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${integrityLoading ? 'animate-spin' : ''}`} />
              {integrityLoading ? 'Checking...' : 'Check Now'}
            </button>
          </div>

          {/* Integrity Results */}
          {integrityData && (
            <div className={`p-4 rounded-xl border ${
              integrityData.totalOrphaned > 0
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            }`}>
              <div className="flex items-start gap-3">
                {integrityData.totalOrphaned > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${
                    integrityData.totalOrphaned > 0
                      ? 'text-amber-700 dark:text-amber-300'
                      : 'text-green-700 dark:text-green-300'
                  }`}>
                    {integrityData.totalOrphaned > 0
                      ? `Found ${integrityData.totalOrphaned} orphaned record(s)`
                      : 'No orphaned data found'}
                  </p>
                  {integrityData.totalOrphaned > 0 && (
                    <div className="mt-2 text-sm text-amber-600 dark:text-amber-400 space-y-1">
                      {integrityData.orphanedStudios > 0 && (
                        <p>• {integrityData.orphanedStudios} orphaned studio config(s)</p>
                      )}
                      {integrityData.orphanedTherapists > 0 && (
                        <p>• {integrityData.orphanedTherapists} orphaned therapist(s)</p>
                      )}
                      {integrityData.orphanedRooms > 0 && (
                        <p>• {integrityData.orphanedRooms} orphaned room(s)</p>
                      )}
                      {integrityData.orphanedClients > 0 && (
                        <p>• {integrityData.orphanedClients} orphaned client(s)</p>
                      )}
                      {integrityData.orphanedBookings > 0 && (
                        <p>• {integrityData.orphanedBookings} orphaned booking(s)</p>
                      )}
                      {integrityData.orphanedServices > 0 && (
                        <p>• {integrityData.orphanedServices} orphaned service(s)</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cleanup Orphans Button */}
          {integrityData && integrityData.totalOrphaned > 0 && (
            <div className="flex items-center gap-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
              <div className="flex-1">
                <p className="font-medium text-red-700 dark:text-red-300">Clean Up Orphaned Data</p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Soft-delete all data belonging to users that no longer exist. This action is reversible by an admin.
                </p>
              </div>
              <button
                onClick={handleCleanupOrphans}
                disabled={cleanupRunning}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                <Trash2 className={`w-4 h-4 ${cleanupRunning ? 'animate-pulse' : ''}`} />
                {cleanupRunning ? 'Cleaning...' : 'Clean Up'}
              </button>
            </div>
          )}

          {/* Cleanup Result */}
          {cleanupResult && (
            <div className={`flex items-center gap-2 p-3 rounded-xl border ${
              cleanupResult.success
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              {cleanupResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <p className={`text-sm ${cleanupResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {cleanupResult.message}
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Notifications</h2>
            <p className="text-sm text-stone-500">Configure admin alerts</p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-800 rounded-xl cursor-pointer">
            <div>
              <p className="font-medium text-stone-700 dark:text-stone-300">New Studio Signups</p>
              <p className="text-sm text-stone-500">Get notified when a new studio registers</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notify_new_studios === 'true'}
              onChange={(e) => updateSetting('notify_new_studios', e.target.checked ? 'true' : 'false')}
              className="w-5 h-5 text-sage rounded"
            />
          </label>
          <label className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-800 rounded-xl cursor-pointer">
            <div>
              <p className="font-medium text-stone-700 dark:text-stone-300">Failed Payments</p>
              <p className="text-sm text-stone-500">Alert when a subscription payment fails</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notify_failed_payments === 'true'}
              onChange={(e) => updateSetting('notify_failed_payments', e.target.checked ? 'true' : 'false')}
              className="w-5 h-5 text-sage rounded"
            />
          </label>
          <label className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-800 rounded-xl cursor-pointer">
            <div>
              <p className="font-medium text-stone-700 dark:text-stone-300">Trial Expirations</p>
              <p className="text-sm text-stone-500">Notify when trials are about to expire</p>
            </div>
            <input
              type="checkbox"
              checked={settings.notify_trial_expirations === 'true'}
              onChange={(e) => updateSetting('notify_trial_expirations', e.target.checked ? 'true' : 'false')}
              className="w-5 h-5 text-sage rounded"
            />
          </label>
        </div>
      </div>

      {/* Booking Reminders (Cron Job) */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Booking Reminders</h2>
            <p className="text-sm text-stone-500">Automated client reminder system (cron job)</p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-800 rounded-xl cursor-pointer">
            <div>
              <p className="font-medium text-stone-700 dark:text-stone-300">Enable Booking Reminders</p>
              <p className="text-sm text-stone-500">
                Send automated SMS/email reminders to clients (24h and 2h before appointments)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2 py-1 rounded-full ${
                settings.reminders_enabled === 'true'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-400'
              }`}>
                {settings.reminders_enabled === 'true' ? 'Active' : 'Paused'}
              </span>
              <input
                type="checkbox"
                checked={settings.reminders_enabled === 'true'}
                onChange={(e) => updateSetting('reminders_enabled', e.target.checked ? 'true' : 'false')}
                className="w-5 h-5 text-sage rounded"
              />
            </div>
          </label>
          <p className="text-xs text-stone-400 px-1">
            Cron job runs every 5 minutes. When disabled, the job still runs but skips processing.
          </p>
        </div>
      </div>

      {/* Email Testing */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Email Testing</h2>
            <p className="text-sm text-stone-500">Test email delivery for booking reminders</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Provider Status */}
          {notificationStatus && (
            <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">Email Provider Status</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${notificationStatus.email.available ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-sm text-stone-600 dark:text-stone-400">
                    {notificationStatus.email.provider === 'none' ? 'Not configured' : notificationStatus.email.provider}
                  </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  notificationStatus.email.available
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                }`}>
                  {notificationStatus.email.available ? 'Ready' : 'Unavailable'}
                </span>
              </div>
            </div>
          )}

          {/* Test Email Form */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder="Enter email address to test..."
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 px-4 py-2 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
            />
            <button
              onClick={handleSendTestEmail}
              disabled={sendingTestEmail || !testEmail}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingTestEmail ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sendingTestEmail ? 'Sending...' : 'Send Test'}
            </button>
          </div>

          {/* Test Result */}
          {emailTestResult && (
            <div className={`flex items-center gap-2 p-3 rounded-xl border ${
              emailTestResult.success
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              {emailTestResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <p className={`text-sm ${emailTestResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {emailTestResult.message}
              </p>
            </div>
          )}

          <p className="text-xs text-stone-400">
            Sends a test booking confirmation email from noreply@serenetouch.app.
          </p>

          {/* Billing Email Tests */}
          <div className="border-t border-stone-200 dark:border-stone-700 pt-4 mt-4">
            <p className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-3">Billing Email Templates</p>
            <p className="text-xs text-stone-500 mb-3">Test the billing notification emails (uses the email address above)</p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleSendBillingTestEmail('trial_expiring')}
                disabled={sendingBillingEmail !== null || !testEmail}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingBillingEmail === 'trial' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                Test Trial Expiring
              </button>
              <button
                onClick={() => handleSendBillingTestEmail('payment_failed')}
                disabled={sendingBillingEmail !== null || !testEmail}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingBillingEmail === 'payment' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                Test Payment Failed
              </button>
            </div>
            {billingEmailResult && (
              <div className={`flex items-center gap-2 p-3 rounded-xl border mt-3 ${
                billingEmailResult.success
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}>
                {billingEmailResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <p className={`text-sm ${billingEmailResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {billingEmailResult.message}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
